import React, { memo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Clock, Target, ListTodo, X, Zap, Calendar } from 'lucide-react';
import { Z_INDEX } from '../../constants/zIndex';

// Helper pour extraire le texte d'une instruction (peut être string ou objet)
const getInstructionText = (instruction) => {
  if (!instruction) return '';
  if (typeof instruction === 'string') return instruction.trim();
  if (typeof instruction === 'object' && instruction.text) return instruction.text.trim();
  if (typeof instruction === 'object' && instruction.name) return instruction.name.trim();
  return '';
};

/**
 * Modal de démarrage de tâche
 * S'affiche quand une nouvelle tâche commence avec:
 * - Récapitulatif de la tâche (nom, durée, instructions)
 * - Bouton "Commencer" qui enregistre l'heure exacte
 * - Animation d'entrée/sortie fluide
 */
export const TaskStartModal = memo(function TaskStartModal({
  task,
  isOpen,
  onStart,
  onDismiss,
  onSnooze
}) {
  const [isStarting, setIsStarting] = useState(false);
  const [countdown, setCountdown] = useState(null);

  // Auto-dismiss après 2 minutes si pas d'action
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      onDismiss?.();
    }, 120000); // 2 minutes

    return () => clearTimeout(timer);
  }, [isOpen, onDismiss]);

  // Countdown après clic sur "Commencer"
  useEffect(() => {
    if (countdown === null) return;

    if (countdown <= 0) {
      const actualStartTime = new Date().toISOString();
      onStart?.(task.id, actualStartTime);
      setIsStarting(false);
      setCountdown(null);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(c => c - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, onStart, task?.id]);

  const handleStart = () => {
    setIsStarting(true);
    setCountdown(3);
  };

  const handleCancelCountdown = () => {
    setIsStarting(false);
    setCountdown(null);
  };

  if (!task) return null;

  // Calculer la durée formatée
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '~30 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h${mins.toString().padStart(2, '0')}` : `${hours}h`;
  };

  // Obtenir les couleurs selon la catégorie
  const getCategoryColor = (category) => {
    const colors = {
      vital: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400', glow: 'shadow-red-500/30' },
      chore: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400', glow: 'shadow-blue-500/30' },
      pleasure: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', text: 'text-emerald-400', glow: 'shadow-emerald-500/30' },
      work: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', glow: 'shadow-amber-500/30' },
      focus: { bg: 'bg-purple-500/20', border: 'border-purple-500/50', text: 'text-purple-400', glow: 'shadow-purple-500/30' },
      default: { bg: 'bg-zinc-500/20', border: 'border-zinc-500/50', text: 'text-zinc-400', glow: 'shadow-zinc-500/30' }
    };
    return colors[category] || colors.default;
  };

  const categoryColors = getCategoryColor(task.category);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onDismiss}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ zIndex: Z_INDEX.MODAL_BACKDROP }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto"
            style={{ zIndex: Z_INDEX.MOOD_CHECK_IN }}
          >
            <div className={`relative bg-zinc-900 rounded-2xl border ${categoryColors.border} shadow-xl ${categoryColors.glow} overflow-hidden`}>
              {/* Header avec animation pulse */}
              <div className={`relative px-6 py-5 ${categoryColors.bg}`}>
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                />

                <button
                  onClick={onDismiss}
                  className="absolute top-4 right-4 p-1 rounded-full bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-700/50 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                    className={`p-3 rounded-xl ${categoryColors.bg} border ${categoryColors.border}`}
                  >
                    <Clock className={`w-6 h-6 ${categoryColors.text}`} />
                  </motion.div>
                  <div>
                    <p className="text-sm text-zinc-400 uppercase tracking-wide">C'est l'heure !</p>
                    <h2 className="text-xl font-bold text-white">{task.task}</h2>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Infos rapides */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-lg">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-zinc-300">{task.time}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-lg">
                    <Target className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-zinc-300">{formatDuration(task.duration)}</span>
                  </div>
                  {task.isAgendaEvent && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                      <Calendar className="w-4 h-4 text-cyan-400" />
                      <span className="text-sm text-cyan-300">Semaine</span>
                    </div>
                  )}
                </div>

                {/* Instructions/sous-tâches */}
                {task.instructions && task.instructions.length > 0 && task.instructions.some(i => getInstructionText(i)) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <ListTodo className="w-4 h-4" />
                      <span className="text-sm font-medium">À faire</span>
                    </div>
                    <ul className="space-y-1 pl-6">
                      {task.instructions.filter(i => getInstructionText(i)).map((instruction, idx) => (
                        <li key={idx} className="text-sm text-zinc-300 flex items-start gap-2">
                          <span className="text-zinc-500 mt-0.5">•</span>
                          <span>{getInstructionText(instruction)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Points */}
                {task.points > 0 && (
                  <div className="flex items-center gap-2 text-amber-400">
                    <Zap className="w-4 h-4" />
                    <span className="text-sm">+{task.points} XP à gagner</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-6 pb-6 space-y-3">
                {isStarting ? (
                  <motion.button
                    initial={{ scale: 1 }}
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    onClick={handleCancelCountdown}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${categoryColors.bg} border ${categoryColors.border} ${categoryColors.text}`}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <motion.div
                        key={countdown}
                        initial={{ scale: 1.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-2xl"
                      >
                        {countdown}
                      </motion.div>
                      <span>Annuler</span>
                    </div>
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleStart}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-lg shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all flex items-center justify-center gap-3"
                  >
                    <Play className="w-6 h-6" fill="currentColor" />
                    <span>Commencer maintenant</span>
                  </motion.button>
                )}

                {onSnooze && (
                  <button
                    onClick={() => onSnooze(task.id, 5)}
                    className="w-full py-3 rounded-xl bg-zinc-800/50 text-zinc-400 font-medium hover:bg-zinc-700/50 hover:text-zinc-300 transition-all"
                  >
                    Reporter de 5 minutes
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

export default TaskStartModal;
